import type { ReactNode } from 'react'

import { sourceBadge } from '../format'
import type { Source } from '../spec/types'

/**
 * The strip above a block: its name, which store answered, how fresh that
 * answer is, and a one-line note.
 *
 * Verbatim from `docs/design/reference/blocks/head.html`. A block with no
 * title runs without the strip entirely — that is the guide's `head: !!b.title`.
 *
 * The source badge renders even when there is no source, as a 10px empty
 * tinted box. That looks like an artifact of the prototype's template engine
 * and it was suppressed here at first — but the span carries height, and
 * dropping it shifted every block on every screen up by 2px against the
 * design. Reproducing it is the cheaper mistake, so it stays.
 */
export function BlockShell({
  title,
  src,
  fresh,
  note,
  children,
}: {
  title?: string | undefined
  src?: Source | undefined
  fresh?: string | undefined
  note?: string | undefined
  children: ReactNode
}) {
  const badge = sourceBadge(src)

  return (
    <div style={{ marginBottom: '16px' }}>
      {title !== undefined && title !== '' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
          <h4
            style={{
              margin: 0,
              fontSize: '12px',
              letterSpacing: '.08em',
              textTransform: 'uppercase',
            }}
          >
            {title}
          </h4>
          <span
            data-tone={badge.tone}
            style={{
              padding: '1px 5px',
              fontSize: '9.5px',
              fontWeight: 800,
              letterSpacing: '.06em',
              textTransform: 'uppercase',
            }}
          >
            {badge.label}
          </span>
          <span style={{ color: 'var(--k-mut2)', fontSize: '11px', fontFamily: 'var(--k-mono)' }}>
            {fresh}
          </span>
          <span style={{ color: 'var(--k-mut)', fontSize: '11.5px', marginLeft: 'auto' }}>
            {note}
          </span>
        </div>
      )}
      {children}
    </div>
  )
}
